import { Phase, Process, ProcessSpecification, Contract, Segment } from "@/types/contract";
import { CompanyTypeValues, CustomerTypeValues } from "@/constants/customer";
import { isCompany, isCustomerTypeSame, isHousehold } from "@/utils/customer";
import { CustomerType } from "@/types/customerType";
import { Product } from "@/types/enumerations";
import { GdprType, NewsletterType } from "@/constants/agreements";
import { FileTypeCode } from "@/constants/attachment";
import { ContractAgreement } from "@/types/agreements";
import { getProduct } from "./product";
import { RecontractingVariant } from "@/types/recontractingRequest";
import { defaultSegmentServiceCompany, defaultSegmentServiceHousehold } from "@/constants/segment";
import { isDateAfterDefinedDate } from "./productionPoint";
import { REQUIRE_ATTACHMENTS_STATUS_PREVIOUS_CUSTOMER } from "@/constants/common";
import { InvoicesSendingTypeValues } from "@/constants/payments";

const B2BForsageFeatureFlag = process.env.NEXT_PUBLIC_B2B_FORSAGE_FEATURE_FLAG === "true";

const resetAgreements = (agreement: ContractAgreement, agreementTypes: string[]) => {
  if (!agreement.agreed || !agreementTypes.includes(agreement.agreementType)) {
    return agreement;
  }
  return {
    ...agreement,
    agreed: false,
  };
};

const resetSegmentForCompany = (contract: Contract) => {
  if (B2BForsageFeatureFlag) {
    if (contract.process === Process.MOBILITY_SERVICES) {
      return Segment.B2C;
    } else {
      return Segment.B2B;
    }
  } else {
    return Segment.B2C;
  }
};

const getContractStartYearNumber = (contract: Contract) => {
  if (contract.contractDetails?.contractStartYear) {
    return contract.contractDetails.contractStartYear;
  }

  const currentYear = new Date().getFullYear();
  if (isDateAfterDefinedDate() && contract.process === Process.VRB) {
    return currentYear + 1;
  }
  if (contract.process === Process.VRB) {
    return currentYear;
  }
  return null;
};

const resetSegmentServiceClassForCompany = (contract: Contract) => {
  if (B2BForsageFeatureFlag) {
    if (contract.process === Process.MOBILITY_SERVICES) {
      return defaultSegmentServiceHousehold;
    } else {
      return defaultSegmentServiceCompany;
    }
  } else {
    return defaultSegmentServiceHousehold;
  }
};

export const getResetContractToHousehold = (contract: Contract, products: Product[]): Contract => {
  const supplyPoint = contract.supplyPoint;
  const contractStartYear = getContractStartYearNumber(contract);
  const productId = contract.contractDetails!.productId;
  const product = getProduct(
    supplyPoint.commodity,
    contract.process,
    CustomerTypeValues.Household,
    contractStartYear,
    products,
    productId
  );

  return {
    ...contract,
    contractDetails: {
      ...contract.contractDetails!,
      productId: product?.id ?? "",
      productDiscountId: product?.value.disId ?? "",
    },
    customer: {
      ...contract.customer,
      name: "",
      surname: "",
      degreeAfterId: "",
      degreeBeforeId: "",
      degreeBeforeId2: "",
      birthDate: null,
      company: "",
      companyType: null,
      customerType: CustomerTypeValues.Household,
      taxPayer: false,
      companyDetails: {
        caseReferenceInsert: "",
        caseReferencePlace: "",
        caseReferenceSection: "",
        cznace: null,
        fromAres: false,
        orgId: "",
        personalNumber: null,
        vatId: "",
        taxPayer: false,
      },
    },
    supplyPoint: {
      ...supplyPoint,
      tariff: null,
    },
    segment: Segment.B2C,
    segmentServiceClass: defaultSegmentServiceHousehold,
    executivePeople: [],
    attachments: contract.attachments.filter(attachment => attachment.fileTypeCode !== FileTypeCode.CAP),
    agreements: contract.agreements.map(agreement => resetAgreements(agreement, [GdprType])),
  };
};

export const getResetContractToTradesman = (contract: Contract, products: Product[]): Contract => {
  const supplyPoint = contract.supplyPoint;
  const contractStartYear = getContractStartYearNumber(contract);
  const productId = contract.contractDetails!.productId;
  const product = getProduct(
    supplyPoint.commodity,
    contract.process,
    CustomerTypeValues.Company,
    contractStartYear,
    products,
    productId
  );
  const isPSProcess = contract.process === Process.PS;

  return {
    ...contract,
    contractDetails: {
      ...contract.contractDetails!,
      productId: isPSProcess ? null : product?.id ?? "",
      productDiscountId: isPSProcess ? null : product?.value.disId ?? "",
    },
    customer: {
      ...contract.customer,
      name: "",
      surname: "",
      degreeAfterId: "",
      degreeBeforeId: "",
      degreeBeforeId2: "",
      birthDate: null,
      company: null,
      companyType: CompanyTypeValues.Tradesman,
      customerType: CustomerTypeValues.Company,
      taxPayer: false,
      companyDetails: {
        caseReferenceInsert: "",
        caseReferencePlace: "",
        caseReferenceSection: "",
        cznace: null,
        fromAres: false,
        orgId: "",
        personalNumber: null,
        vatId: "",
        taxPayer: false,
      },
    },
    supplyPoint: {
      ...supplyPoint,
      tariff: isPSProcess ? supplyPoint.tariff : null,
    },
    segment: resetSegmentForCompany(contract),
    segmentServiceClass: resetSegmentServiceClassForCompany(contract),
    executivePeople: [],
    attachments: contract.attachments.filter(attachment => attachment.fileTypeCode !== FileTypeCode.CAP),
    agreements: contract.agreements.map(agreement => resetAgreements(agreement, [GdprType])),
  };
};

export const getResetContractToCompany = (contract: Contract, products: Product[]): Contract => {
  const supplyPoint = contract.supplyPoint;
  const contractStartYear = getContractStartYearNumber(contract);
  const productId = contract.contractDetails!.productId;
  const product = getProduct(
    supplyPoint.commodity,
    contract.process,
    CustomerTypeValues.Company,
    contractStartYear,
    products,
    productId
  );
  const isPSProcess = contract.process === Process.PS;

  return {
    ...contract,
    contractDetails: {
      ...contract.contractDetails!,
      productId: isPSProcess ? null : product?.id ?? "",
      productDiscountId: isPSProcess ? null : product?.value.disId ?? "",
    },
    customer: {
      ...contract.customer,
      name: "",
      surname: "",
      degreeAfterId: "",
      degreeBeforeId: "",
      degreeBeforeId2: "",
      birthDate: null,
      company: "",
      companyType: CompanyTypeValues.Company,
      customerType: CustomerTypeValues.Company,
      taxPayer: false,
      companyDetails: {
        caseReferenceInsert: "",
        caseReferencePlace: "",
        caseReferenceSection: "",
        cznace: null,
        fromAres: false,
        orgId: "",
        personalNumber: null,
        vatId: "",
        taxPayer: false,
      },
    },
    supplyPoint: {
      ...supplyPoint,
      tariff: isPSProcess ? supplyPoint.tariff : null,
    },
    segment: resetSegmentForCompany(contract),
    segmentServiceClass: resetSegmentServiceClassForCompany(contract),
    attachments: contract.attachments.filter(attachment => attachment.fileTypeCode !== FileTypeCode.CAP),
    agreements: contract.agreements.map(agreement => resetAgreements(agreement, [GdprType, NewsletterType])),
  };
};

export const getContractForCurrentCustomerType = (
  contract: Contract,
  customerType: CustomerType,
  products: Product[]
): Contract => {
  // no change of customer type
  if (isCustomerTypeSame(customerType, contract.customer)) {
    return contract;
  }
  // change of customer happened
  if (customerType === "household") {
    return getResetContractToHousehold(contract, products);
  }
  if (customerType === "tradesman") {
    return getResetContractToTradesman(contract, products);
  }
  return getResetContractToCompany(contract, products);
};

export const isFinishContract = (contract: Contract) => {
  const { process, supplyPoint, processSpecification } = contract;
  return (
    process === Process.VRB &&
    ((supplyPoint.license === true && processSpecification !== ProcessSpecification.ZSP) ||
      (supplyPoint.installedPower ?? 0) > 100)
  );
};

export const shouldCollectGDPRAgreements = (contract: Contract, isContractRequest: boolean) => {
  const hasGdprAgreement = contract.agreements.some(agreement => agreement.agreementType === GdprType);

  if (contract.process === Process.NEW_POINT && contract.phase === Phase.REQUEST) {
    return false;
  }
  if (contract.process === Process.MOBILITY_SERVICES) {
    return false;
  }
  if (isCompany(contract.customer)) {
    return false;
  }
  if (isContractRequest && contract.process !== Process.PS) {
    return false;
  }
  if (contract.process === Process.PS && contract.processReason === "02") {
    return false;
  }
  if (contract.selfService === false && !hasGdprAgreement) {
    return false;
  }
  return !contract.customer.customerId || hasGdprAgreement;
};

export const shouldCollectNewsletterAgreements = (contract: Contract, isContractRequest: boolean): boolean => {
  const hasNewsletterAgreement = contract.agreements.some(agreement => agreement.agreementType === NewsletterType);
  if (contract.process === Process.NEW_POINT && contract.phase === Phase.REQUEST) {
    return false;
  }
  if (contract.process === Process.MOBILITY_SERVICES) {
    return false;
  }
  if (isCompany(contract.customer)) {
    return false;
  }
  if (isContractRequest) {
    return false;
  }

  return !contract.customer.customerId || hasNewsletterAgreement;
};

export const skipMeterReadings = (contract: Contract) => {
  return contract.supplyPoint.ldsOrMeasurementTypeB || [1, 2, 3].includes(contract.supplyPoint.measurementCategory ?? -1);
};

export const isSavedesk = (contract: Contract) => {
  return contract.recontractingRequest?.recontractingVariant === RecontractingVariant.SAVEDESK;
};

export const isAccord = (contract: Contract) => {
  return contract.recontractingRequest?.recontractingVariant === RecontractingVariant.ACCORD;
};

export const isCampaign = (contract: Contract) => {
  return contract.recontractingRequest?.recontractingVariant === RecontractingVariant.CAMPAIGN;
};

export const isOperatorAdhoc = (contract: Contract) => {
  return contract.recontractingRequest?.recontractingVariant === RecontractingVariant.OPERATORADHOC;
};

export const isCustomerAdhoc = (contract: Contract) => {
  return contract.recontractingRequest?.recontractingVariant === RecontractingVariant.CUSTOMERADHOC;
};

export const isAssignedToCore = (contract?: Contract) => {
  return contract?.contractStatus === "assigned_to_core_signed";
};

export const isRequireAttachmentsPreviousCustomer = (contract?: Contract) => {
  return contract?.contractStatus === REQUIRE_ATTACHMENTS_STATUS_PREVIOUS_CUSTOMER;
};

export const isTCRProcessDeath = (contract?: Contract) =>
  contract?.process === Process.TCR && contract.processReason === "demontage_02";

export const isPSPaperRequest = (contract?: Contract) =>
  contract?.process === Process.PS &&
  contract.attachments.some(attachment => attachment.fileTypeCode === FileTypeCode.CCP && attachment.paperRequest);

export const isTCRProcessDismantling = (contract?: Contract) =>
  contract?.process === Process.TCR && contract.processReason === "demontage_01";

export const allowCustomerEditMutation = (contract: Contract, note: string, isContractRequest: boolean) => {
  return !isContractRequest && contract.selfService === false && note !== "";
};

export const isS3PreviousCustomer = (contract?: Partial<Contract>) =>
  contract?.process === Process.CHANGED_WITH_HAND_OVER &&
  contract.contractStatus === "is_editing_previous_customer" &&
  contract.phase === Phase.REQUEST;

export const isTCRDismantlingB2BPostal = (contract: Contract) => {
  const postalInvoiceType = contract.paymentInfo?.sendingInvoicesType === InvoicesSendingTypeValues.Postal;
  return isTCRProcessDismantling(contract) && !isHousehold(contract.customer) && postalInvoiceType;
};

export const isS3PreviousCustomerCompletion = (contract?: Partial<Contract>) =>
  contract?.process === Process.CHANGED_WITH_HAND_OVER &&
  contract.contractStatus === "to_completion_wa_previous_customer" &&
  contract.phase === Phase.REQUEST;

export const isS3PreviousCustomerReturn = (contract?: Partial<Contract>, isAdditionalInfoReturn = false) =>
  isAdditionalInfoReturn && contract?.process === Process.CHANGED_WITH_HAND_OVER;
